import { computed } from 'vue';

export interface OpeningHoursItem {
  day?: string;
  opens?: string;
  closes?: string;
}

export interface OpeningHoursOverride {
  date?: string;
  opens?: string;
  closes?: string;
  label?: string;
  message?: string;
}

export interface DisplayItem {
  displayName: string;
  opens?: string;
  closes?: string;
  isClosed: boolean;
  isToday: boolean;
  label?: string;
  message?: string;
}

export interface NextOpenDisplay {
  opens?: string;
  closes?: string;
  dayOffset: number;
  dayName: string;
}

const NEXT_OPEN_SCAN_DAYS = 14;

const DAY_ORDER = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday'
] as const;

const _today = new Date();

export const todayIso = toLocalDateString(_today);
export const todayDayDisplayName = _today.toLocaleDateString('en-GB', { weekday: 'long' });

const todayDayName = todayDayDisplayName.toLowerCase();

const currentWeekDates = (() => {
  const dow = _today.getDay();
  const monday = new Date(_today);

  monday.setDate(_today.getDate() - ((dow + 6) % 7));

  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    return toLocalDateString(d);
  });
})();

function toLocalDateString(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');

  return `${y}-${m}-${d}`;
}

function extractDatePart(dateString: string): string {
  return dateString.substring(0, 10);
}

function dayToWeekIndex(day?: string): number {
  if (!day) {
    return -1;
  }

  return DAY_ORDER.indexOf(day.toLowerCase() as (typeof DAY_ORDER)[number]);
}

export function isTimeRangeClosed(opens?: string, closes?: string): boolean {
  if (!opens || !closes) {
    return true;
  }

  return opens.startsWith('00:00') &&
         closes.startsWith('00:00');
}

export function isToday(day?: string): boolean {
  return day?.toLowerCase() === todayDayName;
}

export function formatTime(time?: string): string {
  return time ? time.substring(0, 5) : '';
}

export function formatTimeAmPm(time?: string): string {
  if (!time) {
    return '';
  }
  
  const [hourStr, minuteStr] = time.split(':');
  const hour = parseInt(hourStr, 10);
  const minute = parseInt(minuteStr, 10);
  const period = hour < 12 ? 'am' : 'pm';
  const hour12 = hour % 12 || 12;

  return minute === 0 ? `${hour12}${period}` : `${hour12}:${minuteStr}${period}`;
}

export function formatClosingMessage(message: string, nextOpen: NextOpenDisplay | null): string {
  const dayWord = nextOpen ? (nextOpen.dayOffset === 1 ? 'tomorrow' : nextOpen.dayName) : '';

  return message.replace('[day]', dayWord);
}

export function isPastClosingTime(closes?: string): boolean {
  if (!closes) {
    return false;
  }

  const [hour, minute] = closes.split(':').map(Number);
  const closeTime = new Date();

  closeTime.setHours(hour, minute, 0, 0);

  return new Date() >= closeTime;
}

export function useOpeningHours(
  getOpeningHours: () => OpeningHoursItem[],
  getOpeningHoursOverride: () => OpeningHoursOverride[],
) {
  const overridesByDate = computed((): Record<string, OpeningHoursOverride> =>
    getOpeningHoursOverride()
      .filter(o => Boolean(o.date))
      .reduce<Record<string, OpeningHoursOverride>>((acc, o) => {
        acc[extractDatePart(o.date!)] = o;
        return acc;
      }, {})
  );

  const todayDisplay = computed(() => {
    const override = overridesByDate.value[todayIso];

    if (override) {
      return {
        isClosed: isTimeRangeClosed(override.opens, override.closes),
        opens: override.opens,
        closes: override.closes,
        label: override.label,
        message: override.message,
      };
    }

    const todayItem = getOpeningHours().find(item => isToday(item.day));
    
    if (!todayItem) {
      return null;
    }

    return {
      isClosed: isTimeRangeClosed(todayItem.opens, todayItem.closes),
      opens: todayItem.opens,
      closes: todayItem.closes,
      label: undefined,
    };
  });

  const nextOpenDisplay = computed((): NextOpenDisplay | null => {
    for (let dayOffset = 1; dayOffset <= NEXT_OPEN_SCAN_DAYS; dayOffset++) {
      const date = new Date(_today);
      date.setDate(_today.getDate() + dayOffset);

      const dateIso = toLocalDateString(date);
      const dayName = date.toLocaleDateString('en-GB', { weekday: 'long' });
      const override = overridesByDate.value[dateIso];

      if (override) {
        if (!isTimeRangeClosed(override.opens, override.closes)) {
          return { opens: override.opens, closes: override.closes, dayOffset, dayName };
        }

        continue;
      }

      const item = getOpeningHours().find(i => i.day?.toLowerCase() === dayName.toLowerCase());

      if (item && !isTimeRangeClosed(item.opens, item.closes)) {
        return { opens: item.opens, closes: item.closes, dayOffset, dayName };
      }
    }

    return null;
  });

  const displaySchedule = computed((): DisplayItem[] =>
    getOpeningHours().map((item): DisplayItem => {
      const weekIndex = dayToWeekIndex(item.day);
      const weekdayDate = weekIndex >= 0 ? currentWeekDates[weekIndex] : undefined;
      const override = weekdayDate ? overridesByDate.value[weekdayDate] : undefined;

      if (override) {
        return {
          displayName: item.day || '',
          isClosed: isTimeRangeClosed(override.opens, override.closes),
          opens: override.opens,
          closes: override.closes,
          label: override.label,
          message: override.message,
          isToday: isToday(item.day),
        };
      }

      return {
        displayName: item.day || '',
        isClosed: isTimeRangeClosed(item.opens, item.closes),
        opens: item.opens,
        closes: item.closes,
        label: undefined,
        isToday: isToday(item.day),
      };
    })
  );

  return {
    todayDisplay,
    nextOpenDisplay,
    displaySchedule
  };
}
